import { useTranslate } from "@similarweb/i18n-provider";
import { Badge, Card, IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { KpiCardLoading, KpiCardQueryHeader, KpiCardStatusBody } from "app/components/kpi-card/KpiCardChrome";
import { openHooksModalV5 } from "app/services/ModalService";

import { AdsInsightRow, AdsLockedInsightRow, TrafficInsightRow, TrafficLockedInsightRow } from "./AiInsightsRows";
import { AI_INSIGHTS_TRANSLATION_KEYS, AI_TRAFFIC_HOOK_KEY } from "./const";
import { useAiInsights } from "./useAiInsights";

type AiInsightsWidgetProps = {
    params: {
        key: string;
        country: number | string;
        duration: string;
        webSource: string;
        isWWW: boolean | string;
        from?: string;
        to?: string;
        isWindow?: boolean;
    };
    dateRangeText: string;
    countryText: string;
    countryCode: number;
};

export const AiInsightsWidget = ({ params, dateRangeText, countryText, countryCode }: AiInsightsWidgetProps) => {
    const { translate } = useTranslate();
    const { key: domain, country, duration, webSource, isWWW, from, to, isWindow } = params;

    const { canShowCard, isLoading, status, content } = useAiInsights({
        domain,
        country: countryCode,
        duration,
        webSource,
        from,
        to,
        isWindow,
    });

    if (!canShowCard) {
        return null;
    }

    if (isLoading) {
        return (
            <KpiCardLoading
                dataAutomation="ai-insights-widget"
                bodyVariant="lines"
            />
        );
    }

    const cardHeader = (
        <KpiCardQueryHeader
            title={translate(AI_INSIGHTS_TRANSLATION_KEYS.title)}
            tooltip={translate(AI_INSIGHTS_TRANSLATION_KEYS.tooltip)}
            dateRangeText={dateRangeText}
            countryCode={countryCode}
            countryText={countryText}
            selectorTriggerId="ai-insights-widget"
            dataAutomationPrefix="ai-insights-widget"
            badge={
                <Badge
                    type="beta"
                    data-automation="ai-insights-widget-beta"
                />
            }
        />
    );

    let cardBody;

    if (status === "upgrade") {
        cardBody = (
            <Flex
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
                padding="24px"
                gap="8px"
                flex="1"
            >
                <Text fontVariant="subtitle2">{translate(AI_INSIGHTS_TRANSLATION_KEYS.upgradeTitle)}</Text>
                <IconButton
                    type="trial"
                    iconName="sparkles"
                    onClick={() => openHooksModalV5(AI_TRAFFIC_HOOK_KEY)}
                    data-automation="ai-insights-widget-upgrade"
                >
                    {translate(AI_INSIGHTS_TRANSLATION_KEYS.upgradeCta)}
                </IconButton>
            </Flex>
        );
    } else if (status === "error") {
        cardBody = (
            <KpiCardStatusBody
                title="global.error.title"
                dataAutomation="ai-insights-widget-error"
            />
        );
    } else if (status === "empty") {
        cardBody = (
            <KpiCardStatusBody
                title="global.nodata.notavilable"
                dataAutomation="ai-insights-widget-empty"
            />
        );
    } else if (content) {
        cardBody = (
            <Flex
                flexDirection="column"
                justifyContent="flex-start"
                padding="16px 24px"
                gap="16px"
                flex="1"
            >
                {content.isTrafficLocked ? <TrafficLockedInsightRow /> : null}
                {content.traffic ? (
                    <TrafficInsightRow
                        leadingAssistant={content.traffic.leadingAssistant}
                        domain={domain}
                        country={country}
                        duration={duration}
                        webSource={webSource}
                        isWWW={isWWW}
                    />
                ) : null}
                {content.isAdsLocked ? <AdsLockedInsightRow /> : null}
                {content.ads ? (
                    <AdsInsightRow
                        adsDisplay={content.ads}
                        domain={domain}
                        country={country}
                        duration={duration}
                        webSource={webSource}
                        isWWW={isWWW}
                    />
                ) : null}
            </Flex>
        );
    }

    return (
        <Card data-automation="ai-insights-widget">
            {cardHeader}
            {cardBody}
        </Card>
    );
};
